import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SolarIcon from '../icons/SolarIcon';

import AuthService from '../services/auth';
import notificationService from '../services/notification-service';

const COMPLETED_KEY = 'mgo_onboarding_carousel_completed_v1';
const PENDING_KEY = 'mgo_onboarding_carousel_pending_v1';

const BRAND = '#031444';
const GREEN = '#238048';

// Build the brand gradient used by the splash for visual continuity.
const brandGradient = (theme) =>
  theme.palette.mode === 'dark'
    ? `linear-gradient(160deg, ${BRAND} 0%, #0b1220 55%, #0f172a 100%)`
    : `linear-gradient(160deg, ${BRAND} 0%, #0b1f5c 55%, #238048 130%)`;

const HABITANTE_CARDS = [
  {
    key: 'welcome',
    icon: 'home',
    title: '¡Bienvenido a MiGarita.Online!',
    description:
      'Tu condominio en la palma de la mano. Conoce en segundos lo que puedes hacer desde la app.',
    accent: '#238048',
  },
  {
    key: 'visits',
    icon: 'qr-code',
    title: 'Recibe visitas',
    description:
      'Genera códigos QR o registra visitas regulares y compártelos con tus invitados. Recepción las validará automáticamente.',
    accent: '#238048',
  },
  {
    key: 'amenities',
    icon: 'calendar',
    title: 'Reserva amenidades',
    description:
      'Explora las amenidades disponibles y reserva tu lugar en gimnasio, salón de eventos o áreas comunes.',
    accent: '#238048',
  },
  {
    key: 'issues',
    icon: 'shield-warning',
    title: 'Reporta averías',
    description:
      '¿Encontraste un problema en el edificio? Repórtalo rápido desde las Acciones Rápidas y sigue su estado.',
    accent: '#238048',
  },
  {
    key: 'packages',
    icon: 'box',
    title: 'Paquetes y entregas',
    description:
      'Recepción registra tus paquetes y tú ves cuando llegan. Tú decides cuándo pasar a recogerlos.',
    accent: '#238048',
  },
  {
    key: 'notifications',
    icon: 'bell-bing',
    title: 'Notificaciones en tiempo real',
    description:
      'Recibe avisos de visitas, paquetes, averías, reservaciones y anuncios de tu residencial al instante.',
    accent: '#238048',
    cta: 'notifications',
  },
];

const RECEPTOR_CARDS = [
  {
    key: 'welcome',
    icon: 'home',
    title: '¡Bienvenido a Recepción!',
    description:
      'Estas son tus herramientas principales para gestionar visitas y entregas del día.',
    accent: '#238048',
  },
  {
    key: 'hub',
    icon: 'settings',
    title: 'Hub de Recepción',
    description:
      'Ves las visitas programadas del día y escaneas códigos QR desde un solo lugar.',
    accent: '#238048',
  },
  {
    key: 'visits',
    icon: 'qr-code',
    title: 'Registro de visitas',
    description:
      'Registra visitas manualmente a las unidades cuando un invitado llega sin código previo.',
    accent: '#238048',
  },
  {
    key: 'notifications',
    icon: 'bell-bing',
    title: 'Notificaciones en tiempo real',
    description:
      'Recibe avisos de nuevas visitas, paquetes y eventos importantes del residencial.',
    accent: '#238048',
    cta: 'notifications',
  },
];

const DRAG_THRESHOLD = 80;

export default function OnboardingCarousel() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [notifState, setNotifState] = useState('idle'); // idle|requesting|granted|denied|error|unsupported
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

  const role = AuthService.getCurrentUserRole();
  const isTargetRole = role === 'habitante' || role === 'receptor';

  // Pending/completed flags are kept in React state so that an external
  // event (`mgo:onboarding-pending`) dispatched after the consumer sets the
  // sessionStorage flag can trigger a re-render and re-evaluation of the
  // auto-open effect. Without this, a returning user with a persisted
  // jwtToken cookie would have `isTargetRole === true` on mount (so the
  // auto-open effect runs once with pending=null and bails), and a later
  // `sessionStorage.setItem(...)` from Login/AuthBootstrap would not cause
  // the effect to re-run.
  const [pendingFlag, setPendingFlag] = useState(false);
  const [completedFlag, setCompletedFlag] = useState(false);

  const readFlags = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      setCompletedFlag(window.localStorage.getItem(COMPLETED_KEY) === '1');
      setPendingFlag(window.sessionStorage.getItem(PENDING_KEY) === '1');
    } catch {
      // ignore storage errors
    }
  }, []);

  // Read flags on mount and whenever the pending event is dispatched.
  useEffect(() => {
    readFlags();
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('mgo:onboarding-pending', readFlags);
    return () => window.removeEventListener('mgo:onboarding-pending', readFlags);
  }, [readFlags]);

  const cards = useMemo(() => {
    if (role === 'receptor') return RECEPTOR_CARDS;
    return HABITANTE_CARDS;
  }, [role]);

  const markCompleted = useCallback(() => {
    try {
      window.localStorage.setItem(COMPLETED_KEY, '1');
      window.sessionStorage.removeItem(PENDING_KEY);
      setCompletedFlag(true);
      setPendingFlag(false);
    } catch {
      // ignore storage errors
    }
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    markCompleted();
  }, [markCompleted]);

  // Auto-open when a pending flag is set (first login for residents, or a
  // returning user whose pending flag was just set via the
  // `mgo:onboarding-pending` event) and the carousel has not been completed.
  useEffect(() => {
    if (!isTargetRole) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (open) return undefined;
    if (pendingFlag && !completedFlag) {
      const t = setTimeout(() => setOpen(true), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isTargetRole, pendingFlag, completedFlag, open]);

  // Replay support: the existing /tour route dispatches `mgo:start-tour`.
  // For residents we show the carousel (forcing it open regardless of completion);
  // admin/superuser keep the joyride AppTour.
  useEffect(() => {
    if (!isTargetRole) return undefined;
    const handleStart = () => {
      setIndex(0);
      setDirection(0);
      setNotifState('idle');
      setOpen(true);
    };
    window.addEventListener('mgo:start-tour', handleStart);
    return () => window.removeEventListener('mgo:start-tour', handleStart);
  }, [isTargetRole]);

  const paginate = useCallback(
    (dir) => {
      setDirection(dir);
      setIndex((prev) => {
        const next = prev + dir;
        if (next < 0) return 0;
        if (next > cards.length - 1) return cards.length - 1;
        return next;
      });
    },
    [cards.length],
  );

  const handleDragEnd = useCallback(
    (event, info) => {
      if (info.offset.x < -DRAG_THRESHOLD || info.velocity.x < -300) {
        paginate(1);
      } else if (info.offset.x > DRAG_THRESHOLD || info.velocity.x > 300) {
        paginate(-1);
      }
    },
    [paginate],
  );

  const handleActivateNotifications = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setNotifState('unsupported');
      setSnack({ open: true, severity: 'info', message: 'Este dispositivo no soporta notificaciones.' });
      return;
    }
    if (Notification.permission === 'granted') {
      setNotifState('requesting');
      try {
        await notificationService.registerToken();
      } catch (err) {
        // ignore — permission is already granted
      }
      setNotifState('granted');
      setSnack({ open: true, severity: 'success', message: 'Notificaciones activadas correctamente.' });
      return;
    }
    setNotifState('requesting');
    try {
      const success = await notificationService.requestPermissionAndRegister();
      if (success) {
        setNotifState('granted');
        setSnack({ open: true, severity: 'success', message: '¡Listo! Recibirás avisos de visitas, paquetes y más.' });
      } else {
        setNotifState('denied');
        setSnack({
          open: true,
          severity: 'warning',
          message: 'Bloqueaste las notificaciones. Puedes activarlas después desde Perfil.',
        });
      }
    } catch (err) {
      setNotifState('error');
      setSnack({
        open: true,
        severity: 'error',
        message: 'No pudimos activar las notificaciones ahora. Inténtalo más tarde desde Perfil.',
      });
    }
  }, []);

  const isLast = index === cards.length - 1;
  const currentCard = cards[index];

  return (
    <>
      <AnimatePresence>
        {open && (
          <CarouselOverlay
            key="onboarding-carousel"
            onClose={close}
            index={index}
            direction={direction}
            cards={cards}
            currentCard={currentCard}
            onDragEnd={handleDragEnd}
            onPrev={() => paginate(-1)}
            onNext={() => paginate(1)}
            isLast={isLast}
            notifState={notifState}
            onActivateNotifications={handleActivateNotifications}
            gradient={brandGradient(theme)}
          />
        )}
      </AnimatePresence>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function CarouselOverlay({
  onClose,
  index,
  direction,
  cards,
  currentCard,
  onDragEnd,
  onPrev,
  onNext,
  isLast,
  notifState,
  onActivateNotifications,
  gradient,
}) {
  const variants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        background: gradient,
      }}
    >
      {/* Top bar: brand + skip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          color: '#fff',
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.5px', opacity: 0.85 }}>
          MiGarita.Online
        </Typography>
        <Button
          onClick={onClose}
          size="small"
          sx={{
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
          }}
          endIcon={<CloseIcon />}
        >
          Saltar
        </Button>
      </Box>

      {/* Card stage */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <AnimatePresence custom={direction} mode="popLayout" initial={false}>
          <motion.div
            key={currentCard.key}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 260, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={onDragEnd}
            style={{ width: '100%', maxWidth: 460, touchAction: 'pan-y' }}
          >
            <OnboardingCard
              card={currentCard}
              notifState={notifState}
              onActivateNotifications={onActivateNotifications}
            />
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Bottom controls: dots + nav buttons */}
      <Box
        sx={{
          px: 2,
          pb: 3,
          pt: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          {cards.map((c, i) => (
            <Box
              key={c.key}
              sx={{
                width: i === index ? 22 : 7,
                height: 7,
                borderRadius: 999,
                backgroundColor: i === index ? GREEN : 'rgba(255,255,255,0.45)',
                transition: 'width 0.25s ease, background-color 0.25s ease',
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 460, gap: 1 }}>
          <IconButton
            onClick={onPrev}
            disabled={index === 0}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)', backgroundColor: 'transparent' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {isLast ? (
            <Button
              onClick={onClose}
              variant="contained"
              size="large"
              sx={{
                flex: 1,
                ml: 1,
                mr: 1,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                py: 1.1,
                backgroundColor: GREEN,
                '&:hover': { backgroundColor: '#2fa858' },
              }}
              endIcon={<SolarIcon name="check-circle" size={20} />}
            >
              Comenzar
            </Button>
          ) : (
            <Button
              onClick={onNext}
              variant="contained"
              size="large"
              sx={{
                flex: 1,
                ml: 1,
                mr: 1,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                py: 1.1,
                backgroundColor: '#fff',
                color: BRAND,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
              }}
              endIcon={<ChevronRightIcon />}
            >
              Siguiente
            </Button>
          )}

          <IconButton
            onClick={onNext}
            disabled={isLast}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)', backgroundColor: 'transparent' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    </motion.div>
  );
}

function OnboardingCard({ card, notifState, onActivateNotifications }) {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 4,
        px: 3,
        py: 4,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 2,
        boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
      }}
    >
      <Box
        sx={{
          width: 84,
          height: 84,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.14)',
          mb: 1,
        }}
      >
        <SolarIcon name={card.icon} size={44} color={card.accent || '#238048'} />
      </Box>

      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
        {card.title}
      </Typography>

      <Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: 1.55, color: 'rgba(255,255,255,0.82)' }}>
        {card.description}
      </Typography>

      {card.cta === 'notifications' && (
        <NotificationCta state={notifState} onActivate={onActivateNotifications} />
      )}
    </Box>
  );
}

function NotificationCta({ state, onActivate }) {
  if (state === 'granted') {
    return (
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: GREEN,
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        <SolarIcon name="check-circle" size={20} />
        Notificaciones activadas
      </Box>
    );
  }

  if (state === 'denied') {
    return (
      <Box sx={{ mt: 1, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            fontWeight: 500,
            mb: 1,
          }}
        >
          <SolarIcon name="shield-warning" size={18} />
          Bloqueadas — puedes activarlas después en Perfil.
        </Box>
        <Button
          onClick={onActivate}
          variant="outlined"
          size="small"
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
          }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (state === 'error' || state === 'unsupported') {
    return (
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'rgba(255,255,255,0.7)',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <SolarIcon name="shield-warning" size={18} />
        {state === 'unsupported'
          ? 'Este dispositivo no soporta notificaciones.'
          : 'No se pudo activar ahora. Inténtalo en Perfil.'}
      </Box>
    );
  }

  return (
    <Button
      onClick={onActivate}
      variant="contained"
      disabled={state === 'requesting'}
      fullWidth
      sx={{
        mt: 1,
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 999,
        py: 1.1,
        backgroundColor: GREEN,
        '&:hover': { backgroundColor: '#2fa858' },
      }}
      startIcon={<SolarIcon name="bell-bing" size={20} />}
    >
      {state === 'requesting' ? 'Activando…' : 'Activar notificaciones'}
    </Button>
  );
}
